import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Star } from "lucide-react";
import { StatusBadge, RescheduleForm } from "@/components/BookingBits";

const TIER_LABELS = { basic: "Basic", member: "Member", premium: "Premium", vip: "VIP" };

const hoursUntil = (b) => (new Date(`${b.date}T${b.time_slot}:00Z`) - Date.now()) / 36e5;

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [reschedOpen, setReschedOpen] = useState(null);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => setBookings([]));

  useEffect(() => { refresh(); load(); /* eslint-disable-next-line */ }, []);

  const submitReview = async () => {
    try {
      await api.post("/reviews", { booking_id: reviewOpen, rating: reviewRating, content: reviewText });
      setMsg("Thank you — your review is now visible on the practitioner's profile.");
      setReviewOpen(null); setReviewText(""); setReviewRating(5);
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  const cancel = async (b) => {
    const late = b.status === "confirmed" && hoursUntil(b) < 24;
    const ok = window.confirm(late
      ? "This session is less than 24 hours away — cancelling now is non-refundable. Cancel anyway?"
      : "Cancel this session? Any payment or hold will be fully refunded.");
    if (!ok) return;
    try {
      const { data } = await api.post(`/bookings/${b.id}/cancel`);
      setMsg(data.refunded ? "Session cancelled and refunded." : "Session cancelled.");
      load();
    } catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  const upcoming = bookings.filter((b) => ["requested", "confirmed", "pending_payment"].includes(b.status));
  const past = bookings.filter((b) => !["requested", "confirmed", "pending_payment"].includes(b.status));

  const renderBooking = (b) => (
    <div key={b.id} className="tt-card p-6" data-testid={`booking-${b.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-serif text-xl text-[#F7ECEB]">{b.practitioner_name}</div>
          <div className="text-sm text-[#A89896] mt-1">{b.date} · {b.time_slot} · {b.session_type} · {b.duration_minutes}min</div>
          {b.notes && <div className="text-xs text-[#A89896] mt-2 italic">"{b.notes}"</div>}
          {b.status === "requested" && (
            <div className="text-xs text-[#E58B5E] mt-2" data-testid="hold-info">Awaiting {b.practitioner_name.split(" ")[0]}'s response (24h). Full automatic refund if declined or unanswered.</div>
          )}
          {b.status === "cancelled" && <div className="text-xs text-[#A89896] mt-2">{b.refunded ? "Refunded in full." : "No refund applied."}</div>}
        </div>
        <div className="text-right shrink-0">
          <StatusBadge status={b.status} />
          <div className="mt-2 font-serif text-2xl text-[#F7ECEB]">${b.amount?.toFixed(0)}</div>
        </div>
      </div>

      {["requested", "confirmed"].includes(b.status) && (
        <div className="mt-4 border-t border-[#382A2A] pt-4 flex flex-wrap gap-3">
          <button onClick={() => setReschedOpen(reschedOpen === b.id ? null : b.id)} className="text-sm text-[#E58B5E] underline" data-testid={`reschedule-${b.id}`}>Reschedule</button>
          <button onClick={() => cancel(b)} className="text-sm text-[#A89896] underline" data-testid={`cancel-${b.id}`}>Cancel</button>
        </div>
      )}
      {reschedOpen === b.id && <RescheduleForm booking={b} onDone={() => { setReschedOpen(null); setMsg("Session rescheduled."); load(); }} onCancel={() => setReschedOpen(null)} />}

      {["confirmed", "completed"].includes(b.status) && (
        <div className="mt-4 border-t border-[#382A2A] pt-4">
          {reviewOpen === b.id ? (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B] mb-3">Leave a public review</div>
              <div className="flex gap-1 mb-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setReviewRating(n)} data-testid={`star-${n}`}>
                    <Star className={`w-6 h-6 ${n <= reviewRating ? "fill-[#E58B5E] text-[#E58B5E]" : "text-[#382A2A]"}`} />
                  </button>
                ))}
              </div>
              <textarea value={reviewText} onChange={(e) => setReviewText(e.target.value)} rows={3}
                placeholder="How did this session land for you? Your first name and rating will appear on their profile."
                data-testid="review-text" className="w-full px-3 py-2 rounded-lg border border-[#382A2A] bg-[#1F1717]" />
              <div className="flex gap-2 mt-3">
                <button onClick={submitReview} className="btn-primary text-sm" data-testid="review-submit">Publish review</button>
                <button onClick={() => setReviewOpen(null)} className="btn-ghost text-sm">Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setReviewOpen(b.id)} data-testid={`open-review-${b.id}`} className="text-sm text-[#C93B3B] underline">Review this session →</button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="dashboard-page">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <div className="tt-card p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Welcome back</div>
            <h1 className="font-serif text-3xl mt-2 text-[#F7ECEB]">{user?.name}</h1>
            <p className="text-sm text-[#A89896] mt-1">{user?.email}</p>

            <div className="mt-6 p-4 rounded-xl bg-[#110D0D]">
              <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Your tier</div>
              <div className="font-serif text-2xl mt-1 text-[#F7ECEB]">{TIER_LABELS[user?.membership_tier] || "Basic"}</div>
              {user?.membership_tier !== "vip" && (
                <Link to="/membership" className="mt-3 inline-block text-sm text-[#C93B3B] underline" data-testid="dashboard-upgrade">Upgrade tier</Link>
              )}
            </div>

            {user?.practitioner_id && (
              <div className="mt-4 p-4 rounded-xl bg-[#C93B3B] text-white">
                <div className="text-xs uppercase tracking-[0.2em] opacity-80">Practitioner status</div>
                <div className="mt-1 font-medium">Approved & Live</div>
                <p className="text-sm opacity-85 mt-2">Your profile is visible to members. You keep 85% of every booking.</p>
                <Link to="/practitioner" className="mt-3 inline-block text-sm bg-[#F7ECEB] text-[#110D0D] px-4 py-1.5 rounded-full font-medium" data-testid="dashboard-portal-link">Open Portal</Link>
              </div>
            )}
            {user?.role === "pending_cuddler" && (
              <div className="mt-4 p-4 rounded-xl bg-[#110D0D]">
                <div className="text-xs uppercase tracking-[0.2em] text-[#E58B5E]">Practitioner application</div>
                <div className="mt-1 font-medium text-[#F7ECEB]">Under review</div>
                <Link to="/apply" className="text-sm text-[#C93B3B] underline mt-2 inline-block">View status</Link>
              </div>
            )}
            {user?.role === "admin" && (
              <Link to="/admin" className="btn-secondary block text-center mt-4" data-testid="dashboard-admin-link">Open Admin</Link>
            )}
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C93B3B]">Your sessions</div>
          <h2 className="font-serif text-4xl mt-2 text-[#F7ECEB]">Bookings</h2>
          {msg && <div className="text-sm text-[#E58B5E] mt-3" data-testid="dashboard-msg">{msg}</div>}

          {bookings.length === 0 ? (
            <div className="tt-card p-12 mt-8 text-center">
              <p className="text-[#A89896]">You haven't booked a session yet.</p>
              <Link to="/practitioners" className="btn-primary inline-block mt-6" data-testid="dashboard-browse">Browse practitioners</Link>
            </div>
          ) : (
            <>
              <div className="mt-8 space-y-4" data-testid="upcoming-bookings">
                {upcoming.length === 0 && <p className="text-sm text-[#A89896]">No upcoming sessions.</p>}
                {upcoming.map(renderBooking)}
              </div>
              {past.length > 0 && (
                <>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#A89896] mt-12 mb-4">Past & closed</div>
                  <div className="space-y-4" data-testid="past-bookings">{past.map(renderBooking)}</div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
